import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Notification } from '../../../hooks/useNotifications';

interface AdminNotificationPanelProps {
  notifications: Notification[];
  onRefresh: () => void;
}

const categoryOptions = [
  { value: 'new_service', label: 'New Service', icon: 'ri-flashlight-line', color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/25' },
  { value: 'feature',     label: 'Feature',     icon: 'ri-star-line',       color: 'text-ea-blue',  bg: 'bg-ea-blue/10 border-ea-blue/25' },
  { value: 'update',      label: 'Update',      icon: 'ri-refresh-line',    color: 'text-amber-400',bg: 'bg-amber-400/10 border-amber-400/25' },
  { value: 'offer',       label: 'Offer',       icon: 'ri-price-tag-3-line',color: 'text-pink-400', bg: 'bg-pink-400/10 border-pink-400/25' },
  { value: 'welcome',     label: 'Welcome',     icon: 'ri-hand-heart-line', color: 'text-purple-400',bg: 'bg-purple-400/10 border-purple-400/25' },
];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const AdminNotificationPanel = ({ notifications, onRefresh }: AdminNotificationPanelProps) => {
  const [tab, setTab] = useState<'compose' | 'manage'>('compose');

  // Compose form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('update');
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Manage
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const selectedCat = categoryOptions.find((c) => c.value === category) ?? categoryOptions[0];

  const handlePublish = async () => {
    if (!title.trim() || !body.trim()) {
      setPublishMsg({ type: 'error', text: 'Title and message body are required.' });
      return;
    }
    setPublishing(true);
    setPublishMsg(null);
    const { error } = await supabase
      .from('notifications')
      .insert({ title: title.trim(), body: body.trim(), category, is_published: true });
    setPublishing(false);
    if (error) {
      setPublishMsg({ type: 'error', text: error.message || 'Failed to publish. Please try again.' });
    } else {
      setPublishMsg({ type: 'success', text: 'Notification broadcast to all members!' });
      setTitle('');
      setBody('');
      setCategory('update');
      setShowPreview(false);
      onRefresh();
      setTimeout(() => setPublishMsg(null), 4000);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await supabase.from('notifications').delete().eq('id', id);
    setDeletingId(null);
    onRefresh();
  };

  const handleTogglePublish = async (notif: Notification & { is_published?: boolean }) => {
    setTogglingId(notif.id);
    await supabase
      .from('notifications')
      .update({ is_published: !notif.is_published })
      .eq('id', notif.id);
    setTogglingId(null);
    onRefresh();
  };

  return (
    <div className="bg-ea-navy border border-ea-blue/25 rounded-2xl overflow-hidden">
      {/* Admin badge header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-ea-dark-border bg-ea-blue/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/15 border border-ea-blue/30">
            <i className="ri-shield-user-line text-ea-blue text-sm"></i>
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Admin Notification Centre</p>
            <p className="text-ea-gray/60 text-[10px]">Broadcast to all members instantly</p>
          </div>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-ea-blue/15 border border-ea-blue/30 text-ea-blue text-[10px] font-bold uppercase tracking-wider">
          Admin Only
        </span>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-ea-dark-border">
        {[
          { id: 'compose', label: 'Compose New', icon: 'ri-edit-line' },
          { id: 'manage',  label: `Manage (${notifications.length})`, icon: 'ri-list-check-3' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as 'compose' | 'manage')}
            className={`flex items-center gap-2 px-6 py-3.5 text-xs font-semibold transition-colors cursor-pointer border-b-2 whitespace-nowrap ${
              tab === t.id
                ? 'text-ea-blue border-ea-blue'
                : 'text-ea-gray hover:text-white border-transparent'
            }`}
          >
            <i className={`${t.icon} text-sm`}></i>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* ── COMPOSE TAB ── */}
        {tab === 'compose' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Form */}
            <div className="space-y-5">
              {/* Category */}
              <div>
                <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-2">Category</p>
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setCategory(opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        category === opt.value
                          ? `${opt.bg} ${opt.color} border-opacity-60`
                          : 'bg-ea-black border-ea-dark-border text-ea-gray hover:text-white hover:border-ea-blue/30'
                      }`}
                    >
                      <i className={`${opt.icon} text-xs`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-2">
                  Title <span className="text-ea-blue">*</span>
                </p>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. 🚀 New Service: Email Automation is Live"
                  maxLength={120}
                  className="w-full bg-ea-black border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors"
                />
                <p className="text-ea-gray/40 text-[10px] mt-1 text-right">{title.length}/120</p>
              </div>

              {/* Body */}
              <div>
                <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-2">
                  Message Body <span className="text-ea-blue">*</span>
                </p>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, 500))}
                  placeholder="Describe the update in detail. This is what members will read when they expand the notification..."
                  rows={4}
                  className="w-full bg-ea-black border border-ea-dark-border focus:border-ea-blue rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 outline-none transition-colors resize-none"
                />
                <p className="text-ea-gray/40 text-[10px] mt-1 text-right">{body.length}/500</p>
              </div>

              {/* Feedback */}
              {publishMsg && (
                <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
                  publishMsg.type === 'success'
                    ? 'bg-green-500/10 border-green-500/25 text-green-400'
                    : 'bg-red-500/10 border-red-500/25 text-red-400'
                }`}>
                  <i className={`${publishMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} text-sm mt-0.5 shrink-0`}></i>
                  <p className="text-xs leading-relaxed">{publishMsg.text}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-ea-dark-border hover:border-ea-blue/40 text-ea-gray hover:text-white text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${showPreview ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  {showPreview ? 'Hide Preview' : 'Preview'}
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing || !title.trim() || !body.trim()}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  style={{ boxShadow: '0 0 16px rgba(14,165,233,0.2)' }}
                >
                  {publishing ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Broadcasting...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i> Broadcast to All Members</>
                  )}
                </button>
              </div>
            </div>

            {/* Right: Live Preview */}
            <div>
              <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-3">
                {showPreview ? 'Live Preview' : 'Preview (hidden)'}
              </p>
              <div className={`transition-all duration-300 ${showPreview ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                <div className="relative rounded-xl border bg-ea-navy border-ea-blue/30 overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-ea-blue rounded-l-xl" />
                  <div className="px-5 py-4 flex items-start gap-4">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 mt-0.5 ${selectedCat.bg}`}>
                      <i className={`${selectedCat.icon} ${selectedCat.color} text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white text-sm font-semibold leading-snug">
                            {title || 'Your notification title will appear here'}
                          </h3>
                          <span className="w-2 h-2 rounded-full bg-ea-blue shrink-0" />
                        </div>
                        <span className="text-ea-gray/50 text-[10px] whitespace-nowrap">Now</span>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border mb-2 ${selectedCat.bg} ${selectedCat.color}`}>
                        {selectedCat.label}
                      </span>
                      <p className="text-ea-gray text-xs leading-relaxed line-clamp-3">
                        {body || 'Your message body will appear here. Write something informative and engaging for your members...'}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-ea-gray/40 text-[10px] mt-2 text-center">This is exactly how members will see it</p>
              </div>
              {!showPreview && (
                <div className="flex flex-col items-center justify-center h-36 text-center">
                  <i className="ri-eye-line text-ea-gray/30 text-3xl mb-2"></i>
                  <p className="text-ea-gray/40 text-xs">Click &quot;Preview&quot; to see how it will look</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── MANAGE TAB ── */}
        {tab === 'manage' && (
          <div className="space-y-3">
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-notification-off-line text-ea-gray/30 text-3xl mb-3 block"></i>
                <p className="text-ea-gray/50 text-sm">No notifications yet. Compose one above.</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const cat = categoryOptions.find((c) => c.value === notif.category) ?? categoryOptions[0];
                const extended = notif as Notification & { is_published?: boolean };
                return (
                  <div
                    key={notif.id}
                    className="flex items-start gap-4 p-4 bg-ea-black border border-ea-dark-border rounded-xl hover:border-ea-blue/20 transition-colors"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg border shrink-0 mt-0.5 ${cat.bg}`}>
                      <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-1 flex-wrap">
                        <p className="text-white text-xs font-semibold truncate flex-1 min-w-0">{notif.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${cat.bg} ${cat.color}`}>
                          {cat.label}
                        </span>
                      </div>
                      <p className="text-ea-gray/60 text-[10px] line-clamp-1 mb-1">{notif.body}</p>
                      <p className="text-ea-gray/40 text-[10px]">{formatDate(notif.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleTogglePublish(extended)}
                        disabled={togglingId === notif.id}
                        title={extended.is_published === false ? 'Publish' : 'Unpublish'}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                          extended.is_published === false
                            ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                            : 'border-ea-dark-border text-ea-gray hover:text-amber-400 hover:border-amber-400/30'
                        }`}
                      >
                        {togglingId === notif.id ? (
                          <i className="ri-loader-4-line animate-spin text-xs"></i>
                        ) : (
                          <i className={`${extended.is_published === false ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`}></i>
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(notif.id)}
                        disabled={deletingId === notif.id}
                        title="Delete notification"
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-ea-dark-border text-ea-gray hover:text-red-400 hover:border-red-400/30 transition-colors cursor-pointer"
                      >
                        {deletingId === notif.id ? (
                          <i className="ri-loader-4-line animate-spin text-xs"></i>
                        ) : (
                          <i className="ri-delete-bin-line text-xs"></i>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationPanel;
